import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup"; // For validation
import axios from "axios";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import UploadImage from "./uploadImage";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import "./AddBlog.css"; // Import custom CSS for calendar icon styling
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

const AddBlog = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken; 

  const navigate = useNavigate();
  // Breadcrumb items
  const breadcrumbItems = [
    { label: "Master", link: "/user", icon: "fa-solid fa-house" },
    { label: "Content Management", link: "/blog" },
    { label: "Add Blog", link: "/blog/add-blog", active: true },
  ];

  // Initial form values
  const initialValues = {
    title: "",
    author: "",
    content: "",
    excerpt: "",
    publish_date: new Date().toISOString().split("T")[0], // Default to today's date
    meta_title: "",
    meta_description: "",
    tags: "",
    status: "", // Required field for radio buttons
    image_url: "", // For image upload
  };

  // Validation schema
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    author: Yup.string().required("Author is required"),
    content: Yup.string().required("Content is required"),
    excerpt: Yup.string().required("Excerpt is required"),
    publish_date: Yup.string().required("Publish date is required"),
    status: Yup.string().required("Please select a status"), // Validation for radio buttons
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const blogData = {
        ...values,
        publish_date: values.publish_date,
      };
      console.log("Form values:", values); // Debugging log

      const response = await axios.post(
        `${baseUrl}/admin/addBlog`,
        blogData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.statusType === "SUCCESS") {
        showToast("Blog added successfully", { type: "success" });
        setTimeout(() => {
          navigate('/blog');
        }, 2000); // Wait for 2 seconds before navigating
      } else {
        showToast("Failed: " + response.data.text, { type: "error" });
      }
    } catch (error) {
      console.error("Error adding blog:", error);
      showToast("Error occurred while adding blog", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Notification />
      <Container className="mb-4">
        <BreadCrumb items={breadcrumbItems} />
        <Row>
          <Col lg={12} md={12} sm={12}>
            <h3 className="mb-4">Add Blog</h3>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, setFieldValue }) => (
                <Form>
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">
                      Title
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      id="title"
                      name="title"
                      placeholder="Enter blog title"
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="author" className="form-label">
                      Author
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      id="author"
                      name="author"
                      placeholder="Enter author's name"
                    />
                    <ErrorMessage
                      name="author"
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="content" className="form-label">
                      Content
                    </label>
                    <CKEditor
                      editor={ClassicEditor}
                      data=""
                      onChange={(event, editor) => {
                        const content = editor.getData();
                        setFieldValue("content", content); // Update Formik state
                      }}
                    />
                    <ErrorMessage
                      name="content"
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="excerpt" className="form-label">
                      Excerpt
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      id="excerpt"
                      name="excerpt"
                      placeholder="Enter a short excerpt"
                    />
                    <ErrorMessage
                      name="excerpt"
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="meta_title" className="form-label">
                      Meta Title
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      id="meta_title"
                      name="meta_title"
                      placeholder="Enter meta title for SEO"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="meta_description" className="form-label">
                      Meta Description
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      id="meta_description"
                      name="meta_description"
                      placeholder="Enter meta description for SEO"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="tags" className="form-label">
                      Tags
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      id="tags"
                      name="tags"
                      placeholder="Enter tags (comma-separated)"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="publish_date" className="form-label">
                      Publish Date
                    </label>
                    <Field
                      type="date"
                      className="form-control custom-date-input"
                      id="publish_date"
                      name="publish_date"
                    />
                    <ErrorMessage
                      name="publish_date"
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <Field
                          type="radio"
                          className="form-check-input"
                          id="draft"
                          name="status"
                          value="DRAFT"
                        />
                        <label htmlFor="draft" className="form-check-label">
                          Draft
                        </label>
                      </div>
                      <div className="form-check">
                        <Field
                          type="radio"
                          className="form-check-input"
                          id="published"
                          name="status"
                          value="PUBLISHED"
                        />
                        <label
                          htmlFor="published"
                          className="form-check-label"
                        >
                          Published
                        </label>
                      </div>
                    </div>
                    <ErrorMessage
                      name="status"
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <UploadImage
                      baseUrl={baseUrl}
                      onUploadSuccess={(url) => {
                        setFieldValue("image_url", url); 
                        console.log("Image uploaded successfully:", url);
                      }}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Add Blog"}
                  </Button>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddBlog;